import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Share2, Download, Twitter, Facebook, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const ShareModal = ({ isOpen, onClose, figure }) => {
  const [copied, setCopied] = React.useState(false);

  const shareUrl = window.location.origin;
  const shareText = `I just pulled a ${figure?.rarity} ${figure?.character_name} - "${figure?.title}" from Degen Toybox! 🎮✨`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${figure.image_base64}`;
    link.download = `degen-${figure.character_name.toLowerCase().replace(/\s+/g, '-')}.png`;
    link.click();
    toast.success('Image downloaded!');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank');
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(facebookUrl, '_blank');
  };

  if (!figure) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-2 border-neon-pink/30 max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="font-anton text-3xl uppercase text-neon-pink flex items-center gap-2">
            <Share2 className="w-6 h-6" />
            Share Your Pull
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Preview */}
          <div className="relative rounded-xl overflow-hidden border-2 border-white/10">
            <img
              src={`data:image/png;base64,${figure.image_base64}`}
              alt={figure.character_name}
              className="w-full h-auto"
            />
          </div>

          {/* Share Text */}
          <div className="bg-[#121212] p-4 rounded-lg border border-white/10">
            <p className="font-space text-sm text-white/80">{shareText}</p>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleTwitterShare}
              className="bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white font-space uppercase"
              data-testid="share-twitter"
            >
              <Twitter className="w-4 h-4 mr-2" />
              Twitter
            </Button>

            <Button
              onClick={handleFacebookShare}
              className="bg-[#1877F2] hover:bg-[#155db2] text-white font-space uppercase"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4 mr-2" />
              Facebook
            </Button>

            <Button
              onClick={handleCopyLink}
              className="bg-neon-green hover:bg-neon-green/80 text-black font-space uppercase"
              data-testid="copy-link"
            >
              {copied ? (
                <Check className="w-4 h-4 mr-2" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>

            <Button
              onClick={handleDownload}
              className="bg-neon-pink hover:bg-neon-pink/80 text-white font-space uppercase"
              data-testid="download-image"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;